<template>
  <UIModal :show="show" :width="472" @update:show="cancelCallback">
    <template #header>
      <UIModalHeader
        id="modal-header"
        type="primary"
        class="manageCategoryModal"
        :title="t('quiz.category.manageCategories')"
        :description="t('quiz.category.editOrAddNewCategories')"
        @close="cancelCallback"
      >
      </UIModalHeader>
    </template>
    <UIModalContent>
      <HowItWorks></HowItWorks>
      <div>
        <div
          v-for="(option, index) in category"
          :key="index"
          class="my-3 flex items-center text-sm"
        >
          <div class="mr-2 flex flex-1 gap-2">
            <UIInput
              :id="`option-label-${index}`"
              :model-value="option.label"
              class="w-full"
              :disabled="index === 0"
              :placeholder="t('quiz.category.enterCategoryPlaceholder')"
              :status="errors[index] ? 'error' : ''"
              @update:model-value="(value) => categoryRename(value, index)"
            />
          </div>
          <UIButton
            id="remove-option"
            :disabled="index == 0"
            @click="() => prepareToRemoveOption(index)"
          >
            <Trash01Icon class="h-5 w-5" />
          </UIButton>
        </div>

        <UIButton id="add-option" text class="m-2" :disabled="isLastOptionEmpty" @click="addOption">
          <PlusIcon class="mr-1.5 h-5 w-5" />
          {{ t("quiz.category.addCategory") }}
        </UIButton>
      </div>
      <DeleteConfirmationModal
        :show="showDeleteModal"
        :description="t('quiz.category.deleteCategoryAlertMessage')"
        @confirm="removeOption"
        @cancel="cancelRemoveOption"
      />
    </UIModalContent>
    <template #footer>
      <UIModalFooter
        id="modal-footer"
        :positive-text="t('common.submit')"
        :negative-text="t('common.cancel')"
        type="primary"
        @negative-click="cancelCallback"
        @positive-click="positiveClick"
      >
      </UIModalFooter>
    </template>
  </UIModal>
</template>

<script setup lang="ts">
import { useEmitter } from "@/composition/useEmitter";
import { useAppStore } from "@/store/app";
import { PlusIcon, Trash01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UIButton, UIInput, UIModal, UIModalFooter, UIModalHeader } from "@gohighlevel/ghl-ui";
import { cloneDeep } from "lodash";
import { v4 as uuid4 } from "uuid";
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal.vue";
import HowItWorks from "./HowItWorks.vue";
const store = useAppStore();
const emitter = useEmitter();
const { t } = useI18n();
const emit = defineEmits(["update:closeModal"]);
const closeModal = () => emit("update:closeModal");

defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const indexToRemove = ref(null);

const showDeleteModal = ref(false);

const errors = ref<string[]>([]);
const renamedCategory = [];
const category = reactive(cloneDeep(store.category));
const validateOptions = () => {
  errors.value = category.map((option) => {
    if (option.label?.trim() === "" && "edited" in option && option.edited) {
      return t("quiz.category.theOptionFieldIsRequired");
    }
    if (option.label?.trim() === "" && !("edited" in option)) {
      return t("quiz.category.theOptionFieldIsRequired");
    }
    return "";
  });
};

watch(
  () => category,
  () => {
    validateOptions();
  },
  { deep: true }
);

const isLastOptionEmpty = computed(() => {
  const lastOption = category[category.length - 1];
  return lastOption.label?.trim() === "" && lastOption.value?.trim() === "";
});

const addOption = () => {
  category.push({ label: "", value: "", id: uuid4(), edited: false });
  store.anyUnsavedChanges = true;
};

const prepareToRemoveOption = (index) => {
  if (category.length > 1 && !("edited" in category[index])) {
    indexToRemove.value = index;
    showDeleteModal.value = true;
  } else {
    category.splice(index, 1);
  }
};

const categoryRename = (value, index) => {
  if (!("edited" in category[index])) {
    renamedCategory.push(category[index].id);
  } else {
    category[index].edited = true;
  }
  if (index !== 0) {
    category[index].label = value;
    category[index].value = value.trim().replace(/\s/g, "_");
  }
  store.anyUnsavedChanges = true;
};

const removeOption = async () => {
  if (indexToRemove.value !== null) {
    const categoryToBedeleted = category[indexToRemove.value];
    category.splice(indexToRemove.value, 1);
    store.category = category;
    removeCategoryFromAllOptions(categoryToBedeleted);
    store.deleteResultTemplateSection(categoryToBedeleted.id);
    emitter.emit("category:rerender");
  }
  showDeleteModal.value = false;
  store.anyUnsavedChanges = true;
};

const cancelRemoveOption = () => {
  indexToRemove.value = null;
  showDeleteModal.value = false;
};

const cancelCallback = () => {
  closeModal();
};
const positiveClick = () => {
  store.category = category
    .map((cat) => {
      delete cat.edited;
      return cat;
    })
    .filter((cat) => cat.label.trim() !== "");
  renamedCategory.forEach((id) => {
    renameCategoryFromAllOptions(id);
  });
  emitter.emit("category:rerender");
  closeModal();
};

const removeCategoryFromAllOptions = (categoryToBedeleted) => {
  store.slides.forEach((slide) => {
    slide.slideData.forEach((element) => {
      if (element.scoreByCategory) {
        Object.keys(element?.scoreByCategory).forEach((key) => {
          if (element.scoreByCategory[key].categoryId === categoryToBedeleted.id) {
            delete element.scoreByCategory[key];
          }
        });
      }
    });
  });
};
const renameCategoryFromAllOptions = (categoryId) => {
  const cat = store.category.find((c) => c.id === categoryId);
  store.slides.forEach((slide) => {
    slide.slideData.forEach((element) => {
      if (element.scoreByCategory) {
        Object.keys(element?.scoreByCategory).forEach((key) => {
          if (element.scoreByCategory[key].categoryId === categoryId) {
            element.scoreByCategory[key].category = cat.value;
          }
        });
      }
    });
  });
};
</script>

<style scoped>
.manageCategoryModal :deep(.title) {
  padding-bottom: 0px;
}
.manageCategoryModal :deep(.description) {
  margin-top: -5px;
}
</style>
